import React, { useEffect, useState } from "react";
import styles from "./Orders.module.css";
import { Link } from "react-router-dom";
import {
  getAllTransactions,
  refundBoosterTransaction,
  refundPrintTransaction,
  StoreTransactionsData,
} from "../../services/TransactionServices";
import { useTranslation } from "react-i18next";
import globalStyles from "../../styles/globalStyles.module.css";
import { transactionStatusEnum } from "../../enums/transactionStatusEnum";
import { refundReasonEnum } from "../../enums/refundReasonEnum";
import { useAlert } from "../../context/AlertProvider";
import reportStyles from "../../components/Buttons/ReportButton/ReportButton.module.css";
import { getInvoice } from "../../services/TransactionServices";
import { FaDownload } from "react-icons/fa6";
import { shippingStatusEnum } from "../../enums/shippingStatusEnum";

const Orders: React.FC = () => {
  const [transactions, setTransactions] = useState<
    (StoreTransactionsData & { type: string })[]
  >([]);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundTransaction, setRefundTransaction] = useState<
    (StoreTransactionsData & { type: string }) | null
  >(null);

  const { t } = useTranslation();

  useEffect(() => {
    const fetchTransactions = async () => {
      const response = await getAllTransactions();
      setTransactions(response);
    };
    fetchTransactions();
  }, []);

  type GroupedTransactions = {
    [date: string]: (StoreTransactionsData & { type: string })[];
  };

  const groupedTransactions: GroupedTransactions = transactions.reduce(
    (acc, transaction) => {
      const dateStr = new Date(transaction.created_at).toLocaleDateString();
      if (!acc[dateStr]) {
        acc[dateStr] = [];
      }
      acc[dateStr].push(transaction);
      return acc;
    },
    {} as GroupedTransactions
  );

  const openRefundModal = (
    transaction: StoreTransactionsData & { type: string }
  ) => {
    setRefundTransaction(transaction);
    setShowRefundModal(true);
  };

  const closeRefundModal = () => {
    setShowRefundModal(false);
    setRefundTransaction(null);
  };

  // const handleRefundSubmit = async () => {
  //   if (refundTransaction && refundReason.trim()) {
  //     setTransactions((prevTransactions) =>
  //       prevTransactions.map((transaction) =>
  //         transaction.id === refundTransaction.id &&
  //         transaction.booster_id === refundTransaction.boosterId
  //           ? { ...transaction, status: transactionStatusEnum.REFUNDED }
  //           : transaction
  //       )
  //     );

  //     closeRefundModal();
  //   }
  // };

  const handleGetInvoice = async (transactionId: number) => {
    try {
      const invoiceUrl = await getInvoice(transactionId);

      // Create a temporary <a> element
      const link = document.createElement("a");
      link.href = invoiceUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error fetching receipt URL:", error);
    }
  };

  return (
    <div>
      <h1>{t("transactions")}</h1>
      <div className={styles.itemContainer}>
        {/* <div className={styles.item}>
          <p>{t("item")}</p>
          <p>{t("amount")}</p>
          <p>{t("status")}</p>
          <p>{t("action")}</p>
        </div> */}

        {Object.keys(groupedTransactions).map((date) => (
          <div key={date}>
            <h3>{date}</h3>
            {groupedTransactions[date].map((transaction, index) => (
              <div key={index} className={styles.item}>
                <Link
                  to={`/boosters/${transaction.booster_id}`}
                  className={styles.link}
                >
                  <img
                    src={transaction.image_id}
                    alt={transaction.name}
                    className={styles.boosterImage}
                  />
                  <span>{transaction.name}</span>
                </Link>
                <div>
                  {transaction.status === transactionStatusEnum.REFUNDED ? (
                    <span style={{ textDecoration: "line-through" }}>
                      {transaction.quantity}x{transaction.price}€
                    </span>
                  ) : (
                    <span>
                      {transaction.quantity} x {transaction.price}€
                    </span>
                  )}
                </div>
                <div>{transaction.status}</div>
                <div className={styles.actionButtons}>
                  {transaction.status !== transactionStatusEnum.REFUNDED &&
                    Number(transaction.price) !== 0 &&
                    new Date().getTime() -
                      new Date(transaction.created_at).getTime() <
                      14 * 24 * 60 * 60 * 1000 && (
                      <button
                        className={globalStyles.button}
                        onClick={() => openRefundModal(transaction)}
                      >
                        {t("refund")}
                      </button>
                    )}
                  {transaction.type === "print" &&
                    transaction.shipping_status ===
                      shippingStatusEnum.SHIPPED && ( //TODO: add shipping status and shipping link
                      <button className={globalStyles.button}>
                        {t("track")}
                      </button>
                    )}
                  <button
                    className={globalStyles.button}
                    onClick={async () => handleGetInvoice(transaction.id)}
                  >
                    <FaDownload />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {refundTransaction && (
        <RefundModal
          isOpen={showRefundModal}
          handleClose={closeRefundModal}
          refundTransaction={refundTransaction}
        />
      )}
    </div>
  );
};

const RefundModal: React.FC<{
  isOpen: boolean;
  handleClose: () => void;
  refundTransaction: StoreTransactionsData & { type: string };
}> = ({ isOpen, handleClose, refundTransaction }) => {
  const [reason, setReason] = useState<refundReasonEnum>();
  const [comment, setComment] = useState<string>("");
  const { t } = useTranslation();
  const { showAlert } = useAlert();

  // Check if this is the return instructions step
  const isReturnInstructionsStep =
    refundTransaction.status === transactionStatusEnum.REFUND_REQUESTED &&
    refundTransaction.type === "print";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason) {
      return;
    }

    let response;
    if (refundTransaction.type === "digital") {
      response = await refundBoosterTransaction(
        refundTransaction.id,
        refundTransaction.booster_id,
        reason + " : " + comment
      );
    } else {
      response = await refundPrintTransaction(
        refundTransaction.id,
        refundTransaction.booster_id,
        reason + " : " + comment
      );
    }

    if (response) {
      showAlert("order refunded");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      handleClose();
    }
  };

  return (
    <>
      {isOpen && (
        <div
          className={reportStyles.modalOverlay}
          onClick={handleClose}
          role="presentation"
          aria-hidden="true"
        >
          <div
            id="report-modal"
            className={reportStyles.modal}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="modal-title"
            aria-describedby="modal-description"
            onKeyDown={handleKeyDown}
            tabIndex={-1}
          >
            <h2 id="modal-title">
              {isReturnInstructionsStep
                ? "Return Instructions"
                : "Request refund"}
            </h2>
            <div className={styles.refundBoosterContainer}>
              <div>
                <img
                  src={refundTransaction.image_id}
                  alt="Booster"
                  className={styles.refundBoosterImage}
                />
              </div>
              <div className={styles.informations}>
                <div>
                  {t("boosters.preview.digitalBooster")} -{" "}
                  {refundTransaction.name}
                </div>
                <h4>
                  {refundTransaction.quantity} x {refundTransaction.price}€
                </h4>
                <div>{refundTransaction.description}</div>
              </div>
            </div>

            {isReturnInstructionsStep ? (
              // Return Instructions Step
              <div className={reportStyles.formContainer}>
                <h3>Product Return Instructions</h3>

                <div style={{ marginBottom: "16px" }}>
                  <strong>Important Information:</strong>
                  <p>
                    • Returns must be initiated within <strong>14 days</strong>{" "}
                    of the purchase date.
                  </p>
                  <p>
                    • Items must be returned in their original condition and
                    packaging.
                  </p>
                  <p>
                    • Refunds will be issued to the original method of payment.
                  </p>
                </div>

                <div
                  style={{
                    marginBottom: "16px",
                    padding: "12px",
                    backgroundColor: "var(--background-mid-color)",
                    borderRadius: "8px",
                  }}
                >
                  <strong>Return Address:</strong>
                  <p>• 1234 Street Name, City, Country</p>
                </div>
                <button
                  type="button"
                  className={globalStyles.button}
                  onClick={handleClose}
                  style={{ width: "100%" }}
                >
                  Cancel Refund Request
                </button>
                <button
                  type="button"
                  className={globalStyles.button}
                  onClick={handleClose}
                  style={{ width: "100%" }}
                >
                  I Understand – Close
                </button>
              </div>
            ) : (
              // Initial Refund Request Step
              <form
                onSubmit={handleSubmit}
                className={reportStyles.formContainer}
              >
                <label htmlFor="reason">Reason:</label>
                <select
                  id="reason"
                  name="reason"
                  value={reason || ""}
                  onChange={(e) =>
                    setReason(e.target.value as refundReasonEnum)
                  }
                  className={globalStyles.input}
                  required
                >
                  <option value="" disabled>
                    Select a reason
                  </option>
                  <option value={refundReasonEnum.WRONG_ITEM}>
                    {t(refundReasonEnum.WRONG_ITEM)}
                  </option>
                  <option value={refundReasonEnum.NOT_AS_DESCRIBED}>
                    {t(refundReasonEnum.NOT_AS_DESCRIBED)}
                  </option>
                  <option value={refundReasonEnum.ACCIDENTAL_PURCHASE}>
                    {t(refundReasonEnum.ACCIDENTAL_PURCHASE)}
                  </option>
                  <option value={refundReasonEnum.DAMAGED_ITEM}>
                    {t(refundReasonEnum.DAMAGED_ITEM)}
                  </option>
                  <option value={refundReasonEnum.OTHER}>
                    {t(refundReasonEnum.OTHER)}
                  </option>
                </select>

                <label htmlFor="description">{t("comment")}</label>
                <textarea
                  id="description"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={4}
                  placeholder="Provide more details"
                  required
                  className={globalStyles.input}
                />

                <button type="submit" className={globalStyles.button}>
                  Refund
                </button>
                <button
                  type="button"
                  className={globalStyles.button}
                  onClick={handleClose}
                >
                  Cancel
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Orders;
